// src/services/webPush.js -- assinatura Web Push padrao (VAPID), F4 do plano.
//
// Plano §2.2: `getToken(messaging, {vapidKey})` do Firebase e' um `subscribe()`
// do navegador com a chave do PROJETO Firebase; o endpoint que o push service
// devolve fica amarrado aquela chave e so o Firebase consegue pushar nele. Para
// o Django falar direto com o push service (RFC 8291/8292) o aparelho precisa
// reassinar com a NOSSA chave publica -- por isso aqui ha um `unsubscribe()`
// antes do `subscribe()` quando a assinatura existente e' de outra chave.
//
// Nao ha novo pedido de permissao: ela e' por origem e ja esta concedida. Este
// modulo NUNCA chama `Notification.requestPermission()`.
//
// Dual-run: o registro FCM continua intacto. O pareamento dos dois canais no
// backend e' pelo `device_id` (= visitorId do FingerprintJS, o mesmo que
// App.vue:261-264 ja manda no registro FCM); sem ele o aparelho conta como nao
// coberto e recebe pelos dois (notification/views.py _tokens_sem_assinatura_vapid).

const ROTA_ASSINATURA = 'notification/api/webpush-subscription/';

/** Valor de `origem` no corpo do POST: este bundle e' o Modo Hack (anonimo). */
export const ORIGEM_MODOHACK = 'modohack';

// Uma tentativa por carregamento de pagina. `updateFCMToken` e' chamado de ate
// quatro pontos do boot (App.vue:158,180,301,339) e dois `subscribe()`
// simultaneos gerariam dois POST identicos para o mesmo endpoint.
let tentativaDaPagina = null;

/** Chave publica VAPID do build. Vazia = ramo VAPID desligado (flag natural). */
export function chavePublicaVapid() {
  return process.env.VUE_APP_WEBPUSH_PUBLIC_KEY || '';
}

// Mesma forma de log JSON de services/sombra.js: uma linha por evento, nada de
// console.log solto (CLAUDE.md). Local para o modulo nao depender da F2.
function avisar(evento, campos, saida = console) {
  saida.warn(JSON.stringify({ evento, ...campos }));
}

function validarBase64Url(base64url) {
  if (!/^[A-Za-z0-9_-]+$/.test(base64url)) {
    throw new Error(`chave publica VAPID invalida: ${JSON.stringify(base64url)}. `
      + 'Esperado: base64url (A-Z a-z 0-9 - _), a chave P-256 nao comprimida de 65 bytes');
  }
}

/**
 * base64url -> Uint8Array, o unico formato que `PushManager.subscribe` aceita
 * em `applicationServerKey`.
 *
 * Exemplo:
 *   urlBase64ToUint8Array('BBXU8Vejhy...').length  // 65
 */
export function urlBase64ToUint8Array(base64url) {
  validarBase64Url(base64url);
  const preenchimento = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + preenchimento).replace(/-/g, '+').replace(/_/g, '/');
  const binario = atob(base64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i += 1) {
    bytes[i] = binario.charCodeAt(i);
  }
  return bytes;
}

/**
 * True se a assinatura existente ja foi feita com `chaveEsperada`.
 *
 * Navegador que nao expoe `options.applicationServerKey` (Safari antigo) cai no
 * `false`: reassinar de novo custa um POST a mais por abertura, e o backend faz
 * UPSERT por endpoint -- deixar passar uma assinatura do Firebase como se fosse
 * nossa seria pior, porque ela contaria como coberta e nunca receberia push.
 *
 * Exemplo:
 *   mesmaChave(assinatura, urlBase64ToUint8Array(chave))  // true
 */
export function mesmaChave(assinatura, chaveEsperada) {
  const opcoes = assinatura && assinatura.options;
  const atual = opcoes && opcoes.applicationServerKey;
  if (!atual) {
    return false;
  }
  const bytes = new Uint8Array(atual);
  return bytes.length === chaveEsperada.length
    && bytes.every((byte, indice) => byte === chaveEsperada[indice]);
}

/**
 * Corpo do POST, no contrato de notification/views.py WebPushSubscriptionView.
 *
 * Exemplo:
 *   corpoDaAssinatura(assinatura, 'visitor-1', ORIGEM_MODOHACK)
 *   // {endpoint:'https://...', keys:{p256dh, auth}, device_id:'visitor-1', origem:'modohack'}
 */
export function corpoDaAssinatura(assinatura, deviceId, origem) {
  const { endpoint, keys } = assinatura.toJSON();
  return {
    endpoint,
    keys: { p256dh: keys.p256dh, auth: keys.auth },
    device_id: deviceId || null,
    origem,
  };
}

// Memoria minima do que o backend ainda NAO aceitou. Sem ela, um POST que falha
// (offline, 500, JWT expirado no Socios) deixaria o aparelho com uma assinatura
// VAPID que o servidor nao conhece E com o token FCM morto -- o navegador guarda
// UMA assinatura por registro de SW, entao o `subscribe` novo mata o do Firebase.
// Com a marca, a proxima abertura do app reenvia; sem ela, o aparelho ficaria
// sem canal nenhum ate alguem limpar os dados do site.
const MARCA_PENDENTE = 'webpush_assinatura_pendente';

function armazenamento() {
  // try/catch: navegador com dados de site bloqueados levanta no ACESSO, e o
  // `typeof` cobre o Node dos testes, que nao tem localStorage.
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (e) {
    return null;
  }
}

function marcarPendente(endpoint) {
  const caixa = armazenamento();
  if (caixa) {
    caixa.setItem(MARCA_PENDENTE, endpoint);
  }
}

function endpointPendente() {
  const caixa = armazenamento();
  return caixa ? caixa.getItem(MARCA_PENDENTE) : null;
}

// O endpoint fica amarrado a chave usada no subscribe: nao da' para reaproveitar
// a assinatura do Firebase, tem que derrubar e refazer.
async function trocarAssinatura(pushManager, chave, atual) {
  if (atual) {
    await atual.unsubscribe();
  }
  return pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: chave });
}

/**
 * Garante que este aparelho tem assinatura VAPID registrada no Django.
 * Idempotente: reabrir o app com a assinatura certa nao gera POST nenhum.
 * Nunca levanta -- e' chamada no boot e um erro aqui nao pode derrubar a tela.
 *
 * Exemplo:
 *   await assinarWebPush({ registration, publicKey, deviceId: visitorId,
 *                          origem: ORIGEM_MODOHACK, http: api });
 */
export async function assinarWebPush({ registration, publicKey, deviceId, origem, http }) {
  if (!publicKey) {
    return { assinou: false, motivo: 'sem-chave' };
  }
  const pushManager = registration && registration.pushManager;
  if (!pushManager) {
    return { assinou: false, motivo: 'sem-pushmanager' };
  }
  try {
    return await registrarAssinatura({ pushManager, publicKey, deviceId, origem, http });
  } catch (erro) {
    avisar('webpush_assinatura_falhou', { origem, erro: String((erro && erro.message) || erro) });
    return { assinou: false, motivo: 'erro' };
  }
}

async function registrarAssinatura({ pushManager, publicKey, deviceId, origem, http }) {
  const chave = urlBase64ToUint8Array(publicKey);
  const atual = await pushManager.getSubscription();
  if (atual && mesmaChave(atual, chave)) {
    return reenviarSePendente(atual, { deviceId, origem, http });
  }
  const nova = await trocarAssinatura(pushManager, chave, atual);
  await enviarAoBackend(nova, { deviceId, origem, http });
  return { assinou: true, motivo: atual ? 'trocada' : 'nova' };
}

// A marca entra ANTES do POST e so sai depois do 2xx: e' o que distingue
// "assinado e registrado" de "assinado e o backend nao sabe".
async function enviarAoBackend(assinatura, { deviceId, origem, http }) {
  marcarPendente(assinatura.endpoint);
  await http.post(ROTA_ASSINATURA, corpoDaAssinatura(assinatura, deviceId, origem));
  const caixa = armazenamento();
  if (caixa) {
    caixa.removeItem(MARCA_PENDENTE);
  }
}

// Idempotencia com uma excecao so: a assinatura certa cujo POST nunca foi aceito.
async function reenviarSePendente(assinatura, contexto) {
  if (endpointPendente() !== assinatura.endpoint) {
    return { assinou: false, motivo: 'ja-assinado' };
  }
  await enviarAoBackend(assinatura, contexto);
  return { assinou: true, motivo: 'reenviada' };
}

/**
 * Versao de navegador: pega o registro do SW ja registrado em main.js e assina
 * uma unica vez por carregamento. Nunca rejeita.
 *
 * Exemplo:
 *   assinarWebPushNaPagina({ origem: ORIGEM_MODOHACK, deviceId: visitorId, http: api });
 */
export function assinarWebPushNaPagina(opcoes) {
  // Permissao ja concedida e' pre-requisito, nao consequencia: `subscribe` com
  // `userVisibleOnly:true` e permissao `default` pode ABRIR o pop-up em cima do
  // usuario, e a F4 promete zero pop-up novo. A checagem fica ANTES do cache da
  // pagina para que quem acabou de conceder consiga assinar sem recarregar.
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return Promise.resolve({ assinou: false, motivo: 'sem-permissao' });
  }
  if (!tentativaDaPagina) {
    tentativaDaPagina = comRegistroPronto(opcoes).catch((erro) => {
      avisar('webpush_assinatura_falhou', { origem: opcoes.origem, erro: String(erro) });
      return { assinou: false, motivo: 'erro' };
    });
  }
  return tentativaDaPagina;
}

async function comRegistroPronto({ origem, deviceId, http, publicKey = chavePublicaVapid() }) {
  if (!publicKey) {
    return { assinou: false, motivo: 'sem-chave' };
  }
  if (!navigator.serviceWorker) {
    return { assinou: false, motivo: 'sem-service-worker' };
  }
  const registration = await navigator.serviceWorker.ready;
  return assinarWebPush({ registration, publicKey, deviceId, origem, http });
}
